import { useInfiniteQuery } from '@tanstack/react-query'
import { fetchApiWithToken, type WithHydraPagination } from '~/api/utils'
import type { ApplicantExperience } from '~/domains/applicants/types'

export const useApplicantExperiences = (
  userIds?: number[],
  endedSinceDays?: number,
  itemsPerPage = 10
) => {
  return useInfiniteQuery({
    queryKey: ['applicantExperiences', userIds, endedSinceDays, itemsPerPage],
    queryFn: async ({
      pageParam = 1
    }): Promise<WithHydraPagination<ApplicantExperience>> => {
      if (!userIds || userIds.length === 0) {
        return {
          '@context': '/contexts/ApplicantExperience',
          '@id': '/v1/applicant-experiences',
          '@type': 'hydra:Collection',
          'hydra:member': [],
          'hydra:totalItems': 0
        }
      }

      const searchParams = new URLSearchParams()
      searchParams.append('page', String(pageParam))
      searchParams.append('itemsPerPage', String(itemsPerPage))
      searchParams.append('latestOnly', 'true')

      const now = new Date()
      const after = new Date(now)
      const daysSince = endedSinceDays ?? 7
      after.setDate(now.getDate() - daysSince)

      searchParams.append('end[after]', after.toISOString())
      searchParams.append('order[end]', 'desc')
      searchParams.append('createdBy', userIds.join(','))

      const data = await fetchApiWithToken<
        WithHydraPagination<ApplicantExperience>
      >({
        method: 'get',
        url: `/v1/applicant-experiences?${searchParams.toString()}`,
        useLDJson: true
      })

      return data
    },
    getNextPageParam: (lastPage, allPages) => {
      const totalItems = lastPage['hydra:totalItems'] ?? 0
      const loadedItems = allPages.reduce(
        (sum, page) => sum + (page['hydra:member']?.length ?? 0),
        0
      )

      if (loadedItems < totalItems) {
        return allPages.length + 1
      }
      return undefined
    },
    initialPageParam: 1,
    enabled: Boolean(userIds?.length)
  })
}
